import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Mail, Phone, MapPin, Loader2, CheckCircle2, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";

export default function ContactSection({ compact = false }) {
  const [data, setData] = useState({
    full_name: "", email: "", phone: "", company: "",
    sector: "unknown", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.full_name || !data.email || !data.message) {
      toast.error("Merci de renseigner nom, e-mail et message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...data, source: "contact" });
      setDone(true);
      toast.success("Merci — un expert ECONATION vous répond sous 48 h.");
    } catch (err) {
      toast.error("Envoi impossible. Réessayez ou appelez-nous.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-section" className={compact ? "bg-white" : "bg-gradient-to-b from-white to-[#F1F5F9]"}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Parlons de votre projet</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05] mb-6">
              Un chargé d&apos;affaires dédié, sous 48 heures.
            </h2>
            <p className="text-[#475569] leading-relaxed mb-10 max-w-md">
              Industriel, exploitant tertiaire ou gestionnaire de patrimoine — décrivez votre projet,
              nous revenons vers vous avec un premier diagnostic et une estimation de valorisation CEE.
            </p>

            <div className="space-y-4">
              {[
                { icon: Mail, title: "E-mail", value: "contact@econation.fr" },
                { icon: Phone, title: "Téléphone", value: "01 86 90 14 48" },
                { icon: MapPin, title: "Bureaux", value: "Paris · Lyon · Marseille · Toulouse · Lille" },
                { icon: Calendar, title: "Prise de rendez-vous", value: "Visioconférence ou visite de site" },
              ].map((c) => (
                <div key={c.title} data-testid={`contact-info-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-start gap-4 p-4 rounded-xl border border-black/[0.06] bg-white/60 backdrop-blur">
                  <div className="h-10 w-10 rounded-lg bg-[#064E3B] grid place-items-center text-[#10B981] shrink-0">
                    <c.icon className="h-4 w-4" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#94A3B8]">{c.title}</div>
                    <div className="font-display font-semibold text-[#0F172A] text-sm mt-1">{c.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl bg-[#0F172A] text-white p-6 border border-[#10B981]/30">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#10B981] mb-2">Carte des interventions</div>
              <div className="font-display text-lg font-semibold mb-3">Présence nationale</div>
              <p className="text-white/65 text-sm leading-relaxed">
                Équipes terrain dédiées en France métropolitaine et DOM-TOM.
                Intervention sous 14 jours partout sur le territoire.
              </p>
              <div className="mt-4 grid grid-cols-5 gap-1">
                {["Paris", "Lyon", "Marseille", "Toulouse", "Lille"].map((c) => (
                  <span key={c} className="text-center text-[10px] py-1.5 rounded-md bg-white/5 text-white/75">{c}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="rounded-3xl border border-black/[0.06] bg-white p-10 md:p-16 text-center shadow-[0_24px_60px_-30px_rgba(15,23,42,0.15)]">
                <div className="mx-auto h-20 w-20 rounded-full bg-[#10B981]/10 grid place-items-center text-[#10B981] mb-6">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0F172A]">Demande reçue — merci.</h3>
                <p className="text-[#475569] mt-3 max-w-md mx-auto">Un chargé d&apos;affaires ECONATION revient vers vous sous 48 heures ouvrées avec un premier diagnostic.</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={onSubmit} className="rounded-3xl border border-black/[0.06] p-8 md:p-10 bg-white shadow-[0_30px_80px_-40px_rgba(15,23,42,0.2)] space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[#0F172A]" htmlFor="cf-name">Nom complet *</Label>
                    <Input id="cf-name" data-testid="contact-name" required className="mt-2 h-12 border-black/10 focus-visible:ring-[#10B981]" value={data.full_name} onChange={(e) => update("full_name", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-[#0F172A]" htmlFor="cf-email">E-mail professionnel *</Label>
                    <Input id="cf-email" type="email" data-testid="contact-email" required className="mt-2 h-12 border-black/10 focus-visible:ring-[#10B981]" value={data.email} onChange={(e) => update("email", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-[#0F172A]" htmlFor="cf-phone">Téléphone</Label>
                    <Input id="cf-phone" data-testid="contact-phone" className="mt-2 h-12 border-black/10 focus-visible:ring-[#10B981]" value={data.phone} onChange={(e) => update("phone", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-[#0F172A]" htmlFor="cf-company">Entreprise / Patrimoine</Label>
                    <Input id="cf-company" data-testid="contact-company" className="mt-2 h-12 border-black/10 focus-visible:ring-[#10B981]" value={data.company} onChange={(e) => update("company", e.target.value)} />
                  </div>
                </div>

                <div>
                  <Label className="text-[#0F172A]">Secteur</Label>
                  <Select value={data.sector} onValueChange={(v) => update("sector", v)}>
                    <SelectTrigger data-testid="contact-sector" className="mt-2 h-12 border-black/10 focus:ring-[#10B981]">
                      <SelectValue placeholder="Sélectionnez un secteur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="industry" data-testid="contact-sector-industry">Industrie</SelectItem>
                      <SelectItem value="commercial" data-testid="contact-sector-commercial">Tertiaire & Commerce</SelectItem>
                      <SelectItem value="residential" data-testid="contact-sector-residential">Résidentiel</SelectItem>
                      <SelectItem value="unknown" data-testid="contact-sector-other">Je ne sais pas encore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#0F172A]" htmlFor="cf-message">Décrivez votre projet *</Label>
                  <Textarea id="cf-message" data-testid="contact-message" required rows={5} className="mt-2 border-black/10 focus-visible:ring-[#10B981]" value={data.message} onChange={(e) => update("message", e.target.value)} placeholder="Type d'équipements, surface concernée, calendrier visé, contraintes opérationnelles..." />
                </div>

                <Button type="submit" disabled={submitting} data-testid="contact-submit" className="bg-[#0055A4] hover:bg-[#003D7A] text-white font-semibold py-7 px-8 rounded-xl shadow-[0_18px_40px_-16px_rgba(249,115,22,0.6)] w-full sm:w-auto text-base group">
                  {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi en cours</>) : (<>Envoyer ma demande <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
